/**
 * Copy and links for the auth screens.
 * Source: design package `magicbi-auth/README.md` §5 and §9.
 * The legacy auth screens keep their own strings until they are removed.
 */

/** Legal and help destinations, opened in a new tab from the card and page footer. */
export const AUTH_LEGAL_LINKS = {
  terms: "https://magicbi.ai/terms",
  privacy: "https://magicbi.ai/privacy",
  help: "https://magicbi.ai/help",
} as const;

export const AUTH_SHELL_COPY = {
  logoAlt: "MagicBI.ai",
  /** Landmark label for the brand carousel column. */
  visualRegionLabel: "What teams build with MagicBI",
  footerNavLabel: "Legal and help",
  terms: "Terms",
  privacy: "Privacy",
  help: "Help",
  newTabHint: "(opens in a new tab)",
  /** Shown inside the card while a lazy screen loads. */
  loadingLabel: "Loading",
} as const;

/** Routes the redesigned cards link between. */
export const AUTH_ROUTES = {
  signIn: "/login",
  signInSso: "/login/sso",
  /** Landing page for an emailed sign-in link (`?token=`). */
  signInMagic: "/login/magic",
  signUp: "/signup",
  forgotPassword: "/forgot-password",
  resetPassword: "/reset-password",
  verifyEmail: "/verify-email",
  oauthCallback: "/oauth/callback",
  selectOrganization: "/select-organization",
} as const;

/** Sign-in with the email prefilled, so the user doesn't type it twice. */
export const buildAuthSignInHref = (email?: string): string => {
  const trimmed = email?.trim();
  return trimmed
    ? `${AUTH_ROUTES.signIn}?email=${encodeURIComponent(trimmed)}`
    : AUTH_ROUTES.signIn;
};

/** Sign-up with the email prefilled ("Create an account" after a failed sign-in check). */
export const buildAuthSignUpHref = (email?: string): string => {
  const trimmed = email?.trim();
  return trimmed
    ? `${AUTH_ROUTES.signUp}?email=${encodeURIComponent(trimmed)}`
    : AUTH_ROUTES.signUp;
};

/** Field labels shared by every card that asks for an email or password. */
export const AUTH_FIELD_COPY = {
  emailLabel: "Work email",
  emailPlaceholder: "you@company.com",
  passwordLabel: "Password",
  passwordPlaceholder: "Enter your password",
  showPassword: "Show password",
  hidePassword: "Hide password",
  changeEmail: "Change",
  changeEmailAriaLabel: "Change email address",
  firstNameLabel: "First name",
  lastNameLabel: "Last name",
  newPasswordPlaceholder: "At least 8 characters",
  confirmPasswordLabel: "Confirm password",
  confirmPasswordPlaceholder: "Re-enter your password",
  showConfirmPassword: "Show confirm password",
  hideConfirmPassword: "Hide confirm password",
} as const;

/** A1–A3 sign in, plus the undesigned password step. */
export const AUTH_SIGN_IN_COPY = {
  title: "Welcome back",
  subtitle: "Sign in to MagicBI to pick up where your data left off.",
  continue: "Continue",
  /** Not the spec's "Sending link": the check can lead to the password step. */
  continueBusy: "Checking your email",
  divider: "or continue with",
  passwordTitle: "Enter your password",
  passwordSubtitle: "You're signing in as",
  signIn: "Sign in",
  signInBusy: "Signing you in",
  forgotPassword: "Forgot password?",
  footerPrompt: "Don't have an account?",
  footerAction: "Sign up",
  /** Under the email after the check finds no account. */
  createAccount: "Create an account",
  /** Password step: sign in from an emailed link instead. */
  magicLink: "Email me a sign-in link instead",
  magicLinkBusy: "Sending link",
} as const;

/** Social row labels. The visible label is short; the accessible name says what it does. */
export const AUTH_SOCIAL_COPY = {
  google: { label: "Google", ariaLabel: "Continue with Google" },
  microsoft: { label: "Microsoft", ariaLabel: "Continue with Microsoft" },
  sso: { label: "SSO", ariaLabel: "Continue with organization SSO" },
} as const;

/** A4 organization SSO. */
export const AUTH_SSO_COPY = {
  back: "Back",
  title: "Sign in with SSO",
  subtitle:
    "Enter your work email. We will send you to your company's identity provider to finish signing in.",
  submit: "Continue with SSO",
  submitBusy: "Redirecting to your provider",
  info: "Your organization admin sets up SSO. If it is not working, ask your admin or use another sign-in option.",
  footerPrompt: "Not using SSO?",
  footerAction: "See all sign-in options",
} as const;

/** Sign-up email step (README §10 Q5: the sign-in card, retitled). */
export const AUTH_SIGN_UP_COPY = {
  title: "Create your account",
  subtitle: "Turn your data into decks, dashboards and memos in minutes.",
  continue: "Continue",
  continueBusy: "Checking your email",
  footerPrompt: "Already have an account?",
  footerAction: "Sign in",
} as const;

/** Invite links keep today's "{inviter} invited you" heading. */
export const formatAuthInviteTitle = (inviterName: string): string =>
  `${inviterName} invited you`;

export const formatAuthInviteSubtitle = (storyTitle: string): string =>
  `to collaborate on "${storyTitle}"`;

/** Mail apps offered on every "check your inbox" screen (spec B1). */
export const AUTH_MAIL_APP_LINKS = [
  { label: "Open Gmail", href: "https://mail.google.com" },
  { label: "Open Outlook", href: "https://outlook.office.com/mail" },
] as const;

/** Resend block and back link shared by the B1/B2 inbox screens. */
export const AUTH_CHECK_INBOX_COPY = {
  resend: "Resend email",
  resendBusy: "Sending",
  /** Button label during the cooldown: "Resend in 0:30". */
  resendIn: "Resend in",
  hint: "Did not get it? Check your spam folder, or send a new link.",
  /** Helper during the cooldown: "You can send another link in 0:30. This keeps…" */
  cooldownHintLead: "You can send another link in",
  cooldownHintTail: "This keeps your inbox safe from spam.",
  resent: "We sent you a new link. Use the most recent email.",
  /** Screen-reader only: said once when the cooldown ends. */
  resendAvailable: "You can send another link now.",
  backToSignIn: "Back to sign in",
} as const;

/** Screen-reader only: said once when a cooldown starts (never per tick). */
export const formatAuthResendWait = (seconds: number): string =>
  `You can send another link in ${seconds} seconds.`;

/** B1 for sign-up. Verification, not a magic link (README §10 Q1). */
export const AUTH_VERIFY_EMAIL_COPY = {
  title: "Verify your email",
  subtitle: "We sent a verification link to",
  body: "Click the link in that email to verify it is you and continue to MagicBI.",
} as const;

/** B1 for a sign-in link sent from the password step (magic link). */
export const AUTH_MAGIC_LINK_COPY = {
  title: "Check your inbox",
  subtitle: "We sent a sign-in link to",
  body: "Click the link in that email to sign in. It expires in 15 minutes.",
  /** Shown when the send fails without a backend reason. */
  sendFailed: "We could not send a sign-in link. Please try again.",
} as const;

/** `/login/magic?token=` — the emailed link's landing card. */
export const AUTH_MAGIC_LINK_LANDING_COPY = {
  title: "Sign in to MagicBI",
  message: "Continue to finish signing in with your emailed link.",
  continue: "Continue",
  signingIn: "Signing you in",
  failedTitle: "Sign-in failed",
  linkInvalid: "This sign-in link has expired or was already used.",
  sendNewLink: "Send a new link",
  backToSignIn: "Back to sign in",
  /** Shown when the sign-in fails without a backend reason. */
  fallbackError: "Sign-in failed. Please try again.",
} as const;

/** Account setup after the email is verified (not in the design package). */
export const AUTH_SETUP_COPY = {
  title: "Set up your account",
  subtitle: "You're creating an account for",
  submit: "Create account",
  submitBusy: "Creating your account",
} as const;

/** Beta approval gate (not in the design package; keeps today's message). */
export const AUTH_PENDING_COPY = {
  title: "Your account is awaiting approval",
} as const;

/** Forgot password (not in the design package). */
export const AUTH_FORGOT_PASSWORD_COPY = {
  title: "Reset your password",
  subtitle: "Enter your work email and we will send you a link to choose a new password.",
  submit: "Send reset link",
  submitBusy: "Sending link",
  footerPrompt: "Remember your password?",
  footerAction: "Sign in",
  /** Link sent: the B1 layout with reset copy. */
  sentTitle: "Check your inbox",
  sentSubtitle: "We sent a password reset link to",
  sentBody: "Click the link in that email to choose a new password.",
} as const;

/** Reset password from the emailed link (not in the design package). */
export const AUTH_RESET_PASSWORD_COPY = {
  title: "Choose a new password",
  subtitle: "Use at least 8 characters.",
  newPasswordLabel: "New password",
  submit: "Reset password",
  submitBusy: "Updating your password",
  footerPrompt: "Remember your password?",
  footerAction: "Sign in",
  preparing: "Preparing reset form",
  successTitle: "Password updated",
  successMessage: "Your password has been updated. Sign in to continue.",
  successAction: "Sign in",
  invalidTitle: "This reset link does not work",
  invalidAction: "Back to sign in",
} as const;

/** Backend single sign-on error codes (`?error=`) to user-readable messages. */
export const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_not_configured: "Single sign-on is not set up for this email domain. Sign in another way or ask your admin.",
  sso_invalid_state: "Your single sign-on session expired. Please start again.",
  sso_provider_error: "Your identity provider could not complete sign-in. Please try again or contact your admin.",
  sso_email_unverified: "Your identity provider has not verified your email address.",
  sso_email_mismatch: "The email from your identity provider does not match the one you entered.",
  sso_domain_mismatch: "Your email domain is not allowed for this organization's single sign-on.",
  sso_not_provisioned: "You do not have an account in this organization yet. Ask your admin to add you.",
  sso_superadmin_not_allowed: "Platform administrator accounts cannot sign in through an organization's single sign-on. Sign in with your password.",
};

/** Hand-off screens: email verification link and OAuth return. */
export const AUTH_STATUS_COPY = {
  verifyingEmail: "Verifying your email",
  signingIn: "Signing you in",
  signInFailed: "Sign-in failed",
  backToSignIn: "Back to sign in",
} as const;

/** C1–C3 choose your organization. */
export const AUTH_ORG_PICKER_COPY = {
  title: "Choose your organization",
  subtitle: "Select where you want to work.",
  label: "Organization",
  placeholder: "Choose an organization",
  /** Trigger text while the memberships load. */
  loading: "Loading organizations",
  /** Both the search placeholder and its accessible name. */
  search: "Search organizations",
  emptySearch: "No organizations match your search.",
  /** Today's dead-end message for an account with no active tenant. */
  noOrganizations: "No organizations are available for this account.",
  /** Today's fallback when the memberships call fails without a backend reason. */
  loadError: "Couldn't load your organizations.",
  retry: "Try again",
  continue: "Continue to home",
  continueBusy: "Opening workspace",
  signedInAs: "Signed in as",
  signOutPrompt: "Wrong account?",
  signOut: "Sign out",
} as const;

/** "By continuing, you agree to the MagicBI Terms and Privacy Policy." */
export const AUTH_LEGAL_NOTICE = {
  lead: "By continuing, you agree to the MagicBI",
  terms: { label: "Terms", href: AUTH_LEGAL_LINKS.terms },
  conjunction: "and",
  privacy: { label: "Privacy Policy", href: AUTH_LEGAL_LINKS.privacy },
  end: ".",
} as const;

/** Page footer links, in display order. */
export const AUTH_FOOTER_LINKS = [
  { label: AUTH_SHELL_COPY.terms, href: AUTH_LEGAL_LINKS.terms },
  { label: AUTH_SHELL_COPY.privacy, href: AUTH_LEGAL_LINKS.privacy },
  { label: AUTH_SHELL_COPY.help, href: AUTH_LEGAL_LINKS.help },
] as const;

const COPYRIGHT_HOLDER = "MagicBI.ai, Inc.";

/** "© 2026 MagicBI.ai, Inc." — year follows the clock so the footer never goes stale. */
export const formatAuthCopyright = (now: Date = new Date()): string =>
  `© ${now.getFullYear()} ${COPYRIGHT_HOLDER}`;
